/**
 * Представление состояния подписки в интерфейсе.
 *
 * Само состояние вычисляет бэкенд: правило «когда доступ есть» — это
 * бизнес-логика, и повторять её на клиенте значит получить два
 * расходящихся ответа. Здесь только оформление.
 */
import type { AccountState } from '@/shared/api/schemas';

export type Tone = 'positive' | 'warning' | 'critical' | 'neutral';

interface StateView {
  readonly label: string;
  readonly tone: Tone;
  readonly hint: string;
}

const VIEWS: Record<AccountState, StateView> = {
  no_subscription: {
    label: 'Нет подписки',
    tone: 'neutral',
    hint: 'Выберите тариф, чтобы начать пользоваться',
  },
  trial: { label: 'Пробный период', tone: 'positive', hint: 'Доступ открыт' },
  active: { label: 'Активна', tone: 'positive', hint: 'Доступ открыт' },
  expiring_soon: {
    label: 'Скоро истекает',
    tone: 'warning',
    hint: 'Продлите, чтобы доступ не прерывался',
  },
  grace: {
    label: 'Срок истёк',
    tone: 'warning',
    hint: 'Доступ ещё работает, но скоро отключится',
  },
  expired: { label: 'Истекла', tone: 'critical', hint: 'Продлите подписку для восстановления' },
  traffic_exhausted: {
    label: 'Лимит трафика исчерпан',
    tone: 'warning',
    hint: 'Смените тариф или дождитесь сброса',
  },
  banned: { label: 'Доступ ограничен', tone: 'critical', hint: 'Обратитесь в поддержку' },
};

export function describeState(state: AccountState): StateView {
  return VIEWS[state];
}

/**
 * Цвет индикатора состояния.
 *
 * Возвращается токен приложения, а не готовый цвет: палитра описана
 * в одном месте (tokens.css), и смена оформления не требует правок здесь.
 */
export function toneColor(tone: Tone): string {
  switch (tone) {
    case 'positive':
      return 'var(--ok)';
    case 'warning':
      return 'var(--warn)';
    case 'critical':
      return 'var(--danger)';
    default:
      return 'var(--muted)';
  }
}
